import {
  ActionIcon,
  Badge,
  Checkbox,
  Divider,
  Group,
  Paper,
  Select,
  SimpleGrid,
  Stack,
  Text,
  Title,
} from '@mantine/core'
import type { UseFormReturnType } from '@mantine/form'
import { modals } from '@mantine/modals'
import { notifications } from '@mantine/notifications'
import { IconPlus, IconTrash } from '@tabler/icons-react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  INSURANCE_CATEGORIES,
  PLAN_TYPES_BY_CATEGORY,
  type AgreementRow,
  type InsuranceCategory,
} from '@behuman/core-contract'
import { useOrpc } from '@/hooks/useClient'
import { states } from '@/utils/states.ts'
import { logError } from '@/utils/errorUtils'

// A pending (unsaved) agreement row in the organization form. Saved rows come
// from the middleware as Contracts and are edited only by terminating.
export interface AgreementFormRow {
  insuranceCategory: string
  insurancePlanType: string
  national: boolean
  states: string[]
}

const emptyAgreementRow: AgreementFormRow = {
  insuranceCategory: '',
  insurancePlanType: '',
  national: false,
  states: [],
}

const CATEGORY_OPTIONS = INSURANCE_CATEGORIES.map((c) => ({ value: c.value, label: c.label }))

const PLAN_TYPE_LABELS: Record<string, string> = Object.fromEntries(
  Object.values(PLAN_TYPES_BY_CATEGORY).flatMap((planTypes) =>
    planTypes.map((p) => [p.value, p.label]),
  ),
)

const CATEGORY_LABELS: Record<string, string> = Object.fromEntries(
  INSURANCE_CATEGORIES.map((c) => [c.value, c.label]),
)

function planTypeOptions(category: string): { value: string; label: string }[] {
  if (!(category in PLAN_TYPES_BY_CATEGORY)) return []
  return PLAN_TYPES_BY_CATEGORY[category as InsuranceCategory].map((p) => ({
    value: p.value,
    label: p.label,
  }))
}

function coverageLabel(row: Pick<AgreementRow, 'national' | 'states'>): string {
  return row.national ? 'National' : row.states.join(', ')
}

// The slice of the host form this component reads and writes. Mantine form
// types are invariant, so the parent casts its `UseFormReturnType` to this
// shape (see CreateUpdateOrganization).
export type AgreementsHostForm = UseFormReturnType<{ agreements: AgreementFormRow[] }>

interface PlatformAgreementsProps {
  organizationId?: string
  form: AgreementsHostForm
}

export function PlatformAgreements({ organizationId, form }: PlatformAgreementsProps) {
  const orpc = useOrpc()
  const queryClient = useQueryClient()

  const { data } = useQuery(
    orpc.listOrganizationAgreements.queryOptions({
      input: { organizationId: organizationId ?? '' },
      enabled: Boolean(organizationId),
    }),
  )

  const terminate = useMutation(
    orpc.terminateOrganizationAgreement.mutationOptions({
      onSuccess: () => {
        void queryClient.invalidateQueries({ queryKey: orpc.listOrganizationAgreements.key() })
      },
      onError: (error) => {
        logError('super', 'Failed to terminate platform agreement', error)
        notifications.show({
          color: 'red',
          title: 'Termination failed',
          message: error instanceof Error ? error.message : 'Unknown error',
        })
      },
    }),
  )

  const confirmTerminate = (row: AgreementRow) => {
    if (!organizationId) return
    modals.openConfirmModal({
      title: 'Terminate agreement',
      children: (
        <Text size="sm">
          Terminate the {CATEGORY_LABELS[row.insuranceCategory] ?? row.insuranceCategory} /{' '}
          {PLAN_TYPE_LABELS[row.insurancePlanType] ?? row.insurancePlanType} agreement (
          {coverageLabel(row)})? The clinic will stop receiving patient-portal bookings for this
          plan type. The contract history is kept.
        </Text>
      ),
      labels: { confirm: 'Terminate', cancel: 'Cancel' },
      confirmProps: { color: 'red' },
      onConfirm: () => terminate.mutate({ organizationId, contractId: row.contractId }),
    })
  }

  const savedRows = data?.agreements ?? []

  return (
    <>
      <Divider my="sm" />
      <Group justify="space-between" mb="xs">
        <Title order={4}>Platform-Clinic Agreements</Title>
        <ActionIcon
          variant="light"
          size="sm"
          onClick={() => form.insertListItem('agreements', { ...emptyAgreementRow })}
        >
          <IconPlus size={16} />
        </ActionIcon>
      </Group>

      {savedRows.length === 0 && form.values.agreements.length === 0 && (
        <Text size="sm" c="dimmed">
          No agreements configured. This clinic will not receive insurance-routed patient-portal
          bookings.
        </Text>
      )}

      {savedRows.length > 0 && (
        <Stack gap="xs">
          {savedRows.map((row) => {
            const active = row.status === 'executed'
            return (
              <Paper key={row.contractId} withBorder p="xs">
                <Group justify="space-between" wrap="nowrap">
                  <div>
                    <Group gap="xs">
                      <Text size="sm" fw={500}>
                        {CATEGORY_LABELS[row.insuranceCategory] ?? row.insuranceCategory} —{' '}
                        {PLAN_TYPE_LABELS[row.insurancePlanType] ?? row.insurancePlanType}
                      </Text>
                      <Badge size="sm" color={active ? 'green' : 'gray'}>
                        {active ? 'Active' : 'Terminated'}
                      </Badge>
                    </Group>
                    <Text size="xs" c="dimmed">
                      {coverageLabel(row)}
                    </Text>
                  </div>
                  {active && (
                    <ActionIcon
                      variant="light"
                      color="red"
                      size="sm"
                      loading={terminate.isPending}
                      onClick={() => confirmTerminate(row)}
                      title="Terminate agreement"
                    >
                      <IconTrash size={16} />
                    </ActionIcon>
                  )}
                </Group>
              </Paper>
            )
          })}
        </Stack>
      )}

      {form.values.agreements.map((row, index) => (
        <Paper key={index} withBorder p="sm" mt="xs">
          <Stack gap="xs">
            <Group justify="space-between" wrap="nowrap">
              <Group grow style={{ flex: 1 }}>
                <Select
                  label="Insurance Category"
                  placeholder="Select category"
                  data={CATEGORY_OPTIONS}
                  {...form.getInputProps(`agreements.${index}.insuranceCategory`)}
                  onChange={(value) => {
                    form.setFieldValue(`agreements.${index}.insuranceCategory`, value ?? '')
                    // Plan types are constrained by category; a stale selection
                    // from another category must not survive the switch.
                    form.setFieldValue(`agreements.${index}.insurancePlanType`, '')
                  }}
                />
                <Select
                  label="Plan Type"
                  placeholder="Select plan type"
                  data={planTypeOptions(row.insuranceCategory)}
                  disabled={!row.insuranceCategory}
                  {...form.getInputProps(`agreements.${index}.insurancePlanType`)}
                />
              </Group>
              <ActionIcon
                variant="light"
                color="red"
                size="sm"
                mt={24}
                onClick={() => form.removeListItem('agreements', index)}
              >
                <IconTrash size={16} />
              </ActionIcon>
            </Group>

            <Checkbox
              label="National coverage"
              checked={row.national}
              onChange={(event) => {
                const national = event.currentTarget.checked
                form.setFieldValue(`agreements.${index}.national`, national)
                if (national) {
                  form.setFieldValue(`agreements.${index}.states`, [])
                }
              }}
            />
            <Checkbox.Group
              value={row.states}
              onChange={(value) => {
                form.setFieldValue(`agreements.${index}.states`, value)
                if (value.length > 0) {
                  form.setFieldValue(`agreements.${index}.national`, false)
                }
              }}
              error={form.errors[`agreements.${index}.states`]}
            >
              <SimpleGrid cols={5} spacing="xs">
                {states.map((state) => (
                  <Checkbox
                    key={state.value}
                    value={state.value}
                    label={state.value}
                    disabled={row.national}
                  />
                ))}
              </SimpleGrid>
            </Checkbox.Group>
          </Stack>
        </Paper>
      ))}
    </>
  )
}
